import { Field, FormSpy } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { getIn } from 'final-form';

import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { Spinner } from 'UIPackage/MainModule/components/Spinner';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionaryParticipantPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ErknmSubjectAdd } from 'KnmErknmPackage/ErknmSubjectAdd/components/ErknmSubjectAdd';
import {
  getResultDecisionInnLabelBlockId,
  getResultDecisionNoteLawsuitBlockId,
  getResultDecisionNoteLawsuitBlockIdForField,
  getResultDecisionOgrnLabelBlockId,
  getResultDecisionOrganizationNameBlockId,
  getResultDecisionOrganizationTemplateBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormNoteLawsuit } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormNoteLawsuit';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { IKnmErknmOrganizationTemplateSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmOrganizationTemplateSelectOption';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmErknmParticipantPositionsSelectOptions,
  getKnmIsErknmStatusFillInProcess,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { DecisionLinks } from 'KnmErknmPackage/ErknmResultDecisionsModule/DecisionLinks/ErknmLinks';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';
import { KnmViolatedRequirementsNpaItem } from 'KnmPackage/KnmViolatedRequirementsNpa/components/KnmViolatedRequirementsNpaItem';
import { KnmLateFieldsRemark } from 'KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';

import styles from './ErknmResultDecisionsItemContent.module.scss';
import { ErknmResultDecisionDocument } from '../ErknmResultDecisionDocument';
import { ErknmResultDecisionInspectors } from '../ErknmResultDecisionInspectors';
import { ErknmResultDecisionsProsecution } from '../ErknmResultDecisionsProsecution';
import { ReactQuillEditorField } from '../../../../UIPackage/MainModule/components/ReactQuillEditorField';
import { IKnmForm } from '../../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { ExternalSystemSignInfoBlock } from '../../../../CorePackage/MainModule/components/ExternalSystemSignInfoBlock';
import { useShowMore } from '../../../../hooks/useShowMore';
import { getTitleFieldWithRedStar } from '../../../../functions/getTitleFieldWithRedStar';

interface IErknmResultDecisionsItemContentProps {
  acts: IKnmErknmFormAct[] | undefined;
  actsRequirementsNpa: IKnmErknmFormViolatedRequirementsNpa[];
  addRequirementsNpa: (decisionIndex: number, fieldName: string) => void;
  decisionIndex: number;
  eventsErknm?: IKnmErknmFormEvent[] | undefined;
  formRef: IKnmFormRef | undefined;
  id: string;
  isWatchOnly: boolean;
  kindKnmCode: KnmKindCodesEnum | undefined;
  knoOrganizationAddress: IKnoOrganizationDictionary['address'] | undefined;
  knoOrganizationPhone: IKnoOrganizationDictionary['phone'] | undefined;
  knoOrganizationTitle: IKnoOrganizationDictionary['title'] | undefined;
  lateFieldName: string;
  name: string;
  organizations?: IKnmErknmFormSubject[] | undefined;
  organizationsTemplatesSelectOptions: IKnmErknmOrganizationTemplateSelectOption[] | undefined;
  value: IKnmErknmFormResultDecision;
}

interface IErknmResultDecisionsItemContentStoreProps {
  isKnmStatusFillInProcess: boolean;
  participantPositionsSelectOptions: IDictionaryParticipantPositionSelectOption[] | undefined;
}

const ErknmResultDecisionsItemContentWithoutStore = ({
  acts,
  actsRequirementsNpa,
  addRequirementsNpa,
  decisionIndex,
  eventsErknm,
  formRef,
  id,
  isWatchOnly,
  kindKnmCode,
  knoOrganizationAddress,
  knoOrganizationPhone,
  knoOrganizationTitle,
  lateFieldName,
  name,
  organizations,
  organizationsTemplatesSelectOptions,
  value,
  isKnmStatusFillInProcess,
  participantPositionsSelectOptions,
}: IErknmResultDecisionsItemContentProps & IErknmResultDecisionsItemContentStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const { isNew: isNewResultDecision, kindDecision, organizationTemplate, organization } = value;
  const kindDecisionCode = kindDecision?.code;
  const noteLawsuitsBlockId = `${id}.noteLawsuits`;
  const violatedRequirementsNpaFieldName = `${name}.violatedMandatoryRequirements`;

  const isNewOrganizationTemplate = !!organizationTemplate && !organizationTemplate.value;

  const resultDecisionOrganizationsTemplatesSelectOptions:
    | IKnmErknmOrganizationTemplateSelectOption[]
    | undefined = organizationsTemplatesSelectOptions?.filter(organizationTemplateSelectOption => {
    return !(isNewOrganizationTemplate && organizationTemplateSelectOption.id === organization?.listId);
  });

  const addField = (fieldName: string) => () =>
    !isWatchOnly && push && push(`${name}.${fieldName}`, { listId: uuidv4(), isNew: true });
  const addInspectorField = (fieldName: string) => () =>
    !isWatchOnly && push && push(`${name}.${fieldName}`, undefined);
  const makeRemoveField = (fieldName: string, index: number) => () =>
    !isWatchOnly && remove && remove(`${name}.${fieldName}`, index);

  const removeViolatedRequirementsNpa = (npaIndex: number) => {
    remove && remove(violatedRequirementsNpaFieldName, npaIndex);
  };

  const formValue = getIn(formRef?.current.getState().values as IKnmForm, name);

  return (
    <>
      <ErknmResultDecisionDocument
        isWatchOnly={isWatchOnly}
        name={`${name}.document.attachments`}
        attachmentsNumber={value.document?.attachments?.length || 0}
        isRequired={!isKnmStatusFillInProcess}
        decisionIndex={decisionIndex}
        formRef={formRef}
        id={`${id}.document.attachments`}
        lateFieldName={
          value.document?.attachments && value.document?.attachments[0]?.guid
            ? `${lateFieldName}/document/attachments/${value.document?.attachments[0]?.guid}/fileName`
            : undefined
        }
      />

      {value.kindDecision?.linkOther && <DecisionLinks links={value.links} watchOnly={isWatchOnly} name={name} />}

      {kindDecisionCode !== KindResultDecisionCodesEnum.D_7 && kindDecisionCode !== KindResultDecisionCodesEnum.D_1 && (
        <FieldBlock
          label="Описание решения"
          id={noteLawsuitsBlockId}
          button={
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              disabled={
                isWatchOnly ||
                ((kindDecisionCode === KindResultDecisionCodesEnum.D_2 ||
                  kindDecisionCode === KindResultDecisionCodesEnum.D_5 ||
                  kindDecisionCode === KindResultDecisionCodesEnum.D_3) &&
                  value.noteLawsuits &&
                  value.noteLawsuits.length > 0)
              }
              onClick={addField('noteLawsuits')}
            />
          }
          tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/noteLawsuits`} />}
        >
          <FieldArray name={`${name}.noteLawsuits`}>
            {({ fields }: FieldArrayRenderProps<IKnmErknmFormNoteLawsuit, HTMLElement>) => {
              if (!fields.length) {
                return null;
              }

              return (
                <Row>
                  {fields.map((fieldName, index) => {
                    const fieldValue: IKnmErknmFormNoteLawsuit = fields.value?.[index];
                    const noteLawsuitId = getResultDecisionNoteLawsuitBlockId(id, fieldValue.listId);

                    return (
                      <Row key={noteLawsuitId} margin="medium" isListItem>
                        <ReactQuillEditorField
                          id={getResultDecisionNoteLawsuitBlockIdForField(
                            id,
                            fieldValue.listId,
                            'valueReactQuillEditor',
                          )}
                          name={`${fieldName}.valueReactQuillEditor`}
                          placeholder="Введите значение"
                          disabled={isWatchOnly}
                          className={styles.ReactQuillEditorField}
                          handleCustomChange={(e: string) => {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            formRef?.current?.change(`${fieldName}.value` as any, e);
                          }}
                        />
                        <CloseButton
                          className="shared-deleting-row-btn"
                          onClick={makeRemoveField('noteLawsuits', index)}
                        />
                        <KnmLateFieldsRemark
                          lateFieldPath={`${lateFieldName}/noteLawsuits/${fieldValue.listId}/value`}
                          fieldId={`${noteLawsuitId}.value`}
                          isLeftPosition={true}
                        />
                      </Row>
                    );
                  })}
                </Row>
              );
            }}
          </FieldArray>
        </FieldBlock>
      )}
      {(kindDecisionCode === KindResultDecisionCodesEnum.D_7 ||
        kindDecisionCode === KindResultDecisionCodesEnum.D_1) && (
        <>
          <Row title="Срок исполнения решения" notSpace id={`${id}.injunction.dateResolved`}>
            <DatePickerField
              className={styles.ErknmResultDecisionsItemContentInjunctionDate}
              name={`${name}.injunction.dateResolved`}
              disabled={isWatchOnly}
            />
            <KnmLateFieldsRemark
              lateFieldPath={`${lateFieldName}/injunction/dateResolved`}
              fieldId={`${id}.injunction.dateResolved`}
            />
          </Row>
          <Row
            title={
              <>
                Информация о проведении мероприятий по предотвращению причинения вреда (ущерба) охраняемым законом
                ценностям
                <KnmLateFieldsRemark
                  lateFieldPath={`${lateFieldName}/injunction/note`}
                  isLeftPosition={true}
                  fieldId={`${id}.injunction.note`}
                />
              </>
            }
          >
            <ReactQuillEditorField
              name={`${name}.injunction.noteReactQuillEditor`}
              className={styles.TextareaFieldFull}
              disabled={isWatchOnly}
              handleCustomChange={(e: string) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formRef?.current?.change(`${name}.injunction.note` as any, e);
              }}
            />
          </Row>
        </>
      )}
      <div className={styles.FieldInfo}>
        <p className={styles.FieldInfoItem}>{knoOrganizationTitle}</p>
        <p className={styles.FieldInfoItem}>
          Телефон: {knoOrganizationPhone || 'отсутствует'}, Адрес: {knoOrganizationAddress || 'отсутствует'}
        </p>
      </div>
      <Row>
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label: 'Номер решения',
            field: `${name}.numberDecision`,
            isKnm: true,
          })}
          placementLabel="left"
          color="black"
          size="half"
          id={`${id}.numberDecision`}
          tooltip={
            <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/numberDecision`} fieldId={`${id}.numberDecision`} />
          }
        >
          <TextInputField className={styles.TextareaField} name={`${name}.numberDecision`} disabled={isWatchOnly} />
        </FieldBlock>
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label: 'Дата решения',
            field: `${name}.dateDecision`,
            isKnm: true,
          })}
          placementLabel="left"
          color="black"
          size="half"
          id={`${id}.dateDecision`}
          tooltip={
            <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/dateDecision`} fieldId={`${id}.dateDecision`} />
          }
        >
          <DatePickerField name={`${name}.dateDecision`} disabled={isWatchOnly} />
        </FieldBlock>
      </Row>
      {formValue?.systemSignatureUsed ? (
        <Row>
          <ExternalSystemSignInfoBlock
            systemSignatureDateTime={formValue.systemSignatureDateTime as string}
            systemSignatureTitle={formValue.systemSignatureTitle}
          />
        </Row>
      ) : (
        <>
          <Row title="Должностное лицо контрольного (надзорного) органа, подписавшее (осуществившее) решение (действие)">
            <FieldBlock
              label={getTitleFieldWithRedStar({
                label: 'ФИО подписавшего решение',
                field: `${name}.fioSigner`,
                isKnm: true,
              })}
              color="black"
              size="half"
              id={`${id}.fioSigner`}
              tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/fioSigner`} fieldId={`${id}.fioSigner`} />}
            >
              <TextInputField name={`${name}.fioSigner`} disabled={isWatchOnly} />
            </FieldBlock>
            <FieldBlock
              label={getTitleFieldWithRedStar({
                label: 'Должность лица, подписавшего решение',
                field: `${name}.titleSigner`,
                isKnm: true,
              })}
              color="black"
              size="half"
              id={`${id}.titleSigner`}
              tooltip={
                <KnmLateFieldsRemark
                  lateFieldPath={`${lateFieldName}/titleSigner/title`}
                  fieldId={`${id}.titleSigner`}
                />
              }
            >
              <SelectInputField
                name={`${name}.titleSigner`}
                placeholder="Выберите должность"
                options={participantPositionsSelectOptions}
                isSearchable
                disabled={isWatchOnly}
                size="medium"
              />
            </FieldBlock>
          </Row>
        </>
      )}
      <ErknmResultDecisionInspectors
        acts={acts}
        id={id}
        name={name}
        value={value}
        decisionIndex={decisionIndex}
        watchOnly={isWatchOnly}
        addField={addField}
        addNewField={addInspectorField}
        makeRemoveField={makeRemoveField}
        lateFieldName={lateFieldName}
      />
      {(kindKnmCode === KnmKindCodesEnum.RO || !isNewResultDecision) && (
        <div className={styles.SubjectAdd}>
          <ErknmSubjectAdd
            id={id}
            watchOnly={isWatchOnly}
            kindKnmCode={kindKnmCode}
            organizationsTemplatesSelectOptions={resultDecisionOrganizationsTemplatesSelectOptions}
            name={name}
            organizationTemplate={value.organizationTemplate}
            organizationTemplateBlockId={getResultDecisionOrganizationTemplateBlockId(id)}
            inn={value.organization?.inn}
            innLabelBlockId={getResultDecisionInnLabelBlockId(id)}
            ogrn={value.organization?.ogrn}
            ogrnLabelBlockId={getResultDecisionOgrnLabelBlockId(id)}
            firstOrganizationName={value.organization?.firstOrganizationName}
            organizationNameBlockId={getResultDecisionOrganizationNameBlockId(id)}
            checkSubjectResult={value.organization?.checkSubjectResult || null}
            isAutocomplete={value.organization?.isAutocomplete}
            subjectType={value.organization?.subjectType}
            kpp={value.organization?.kpp}
            smevRequestStatus={value.organization?.smevRequestStatus}
            rafpNza={value.organization?.rafpNza}
            isSonko={value.organization?.isSonko}
            snils={value.organization?.snils}
            ervkId={value.organization?.ervkId}
            isPresentInItRegistry={value.organization?.isPresentInItRegistry}
            itAccreditationResponse={value.organization?.itAccreditationResponse}
            smevItAccreditationRequestStatus={value.organization?.smevItAccreditationRequestStatus}
            okveds={value.organization?.okveds}
            mspCode={value.organization?.mspCode}
            mspRegistryDate={value.organization?.mspRegistryDate}
          />
        </div>
      )}
      {(kindDecisionCode === KindResultDecisionCodesEnum.D_7 ||
        kindDecisionCode === KindResultDecisionCodesEnum.D_1) && (
        <FieldBlock
          label="Устраняемые нарушения обязательных требований"
          button={
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              disabled={isWatchOnly}
              onClick={() => addRequirementsNpa(decisionIndex, violatedRequirementsNpaFieldName)}
            />
          }
        >
          <FormSpy subscription={{ errors: true, touched: true }}>
            {props => {
              const fieldError = getFieldArrayErrorMessage(
                props.errors?.resultDecisions?.[decisionIndex]?.violatedMandatoryRequirements,
              );
              const isFieldTouched = props.touched?.places;

              if (isFieldTouched && fieldError) {
                return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
              }

              return null;
            }}
          </FormSpy>
          <FieldArray name={violatedRequirementsNpaFieldName}>
            {({ fields }) => {
              const visibleFieldsValues = fields.value?.filter((fieldValue: IKnmErknmFormViolatedRequirementsNpa) => {
                const correspondingActRequirement = actsRequirementsNpa.find(
                  requirement => requirement.npa.id === fieldValue.npa.id,
                );

                return !!correspondingActRequirement;
              });

              const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(
                visibleFieldsValues,
                {
                  initialCount: 10,
                  incrementCount: 10,
                },
              );

              return (
                <>
                  {visibleItems.map((fieldValue: IKnmErknmFormViolatedRequirementsNpa) => {
                    const npaIndex =
                      fields.value?.findIndex((item: IKnmErknmFormViolatedRequirementsNpa) => item === fieldValue) ?? 0;
                    const npaName = `${violatedRequirementsNpaFieldName}[${npaIndex}]`;
                    const correspondingActRequirement = actsRequirementsNpa.find(
                      requirement => requirement.npa.id === fieldValue.npa.id,
                    );

                    if (!correspondingActRequirement) {
                      return null;
                    }

                    return (
                      <Field key={npaName} name={npaName}>
                        {({ meta }) => {
                          const isFieldTouched = meta.touched;
                          const errorField = meta.error;

                          return (
                            <KnmViolatedRequirementsNpaItem
                              value={fieldValue}
                              name={npaName}
                              watchOnly={isWatchOnly}
                              isViolationFieldsWatchOnly={isWatchOnly}
                              onRemove={() => removeViolatedRequirementsNpa(npaIndex)}
                              addMandatory={() => undefined}
                              touched={isFieldTouched}
                              error={errorField}
                              formRef={formRef}
                              isMandatoryDisabled={true}
                              isInsideDecisionsSection
                              actRequirement={correspondingActRequirement}
                            />
                          );
                        }}
                      </Field>
                    );
                  })}
                  {isRendering && <Spinner className={styles.Loader} />}
                  {hasMore && !isRendering && (
                    <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
                  )}
                </>
              );
            }}
          </FieldArray>
        </FieldBlock>
      )}
      <ErknmResultDecisionsProsecution
        acts={acts}
        formRef={formRef}
        id={id}
        name={name}
        value={value}
        eventsErknm={eventsErknm}
        organizations={organizations}
        addField={addField}
        makeRemoveField={makeRemoveField}
        isWatchOnly={isWatchOnly}
        lateFieldName={lateFieldName}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmResultDecisionsItemContentStoreProps => ({
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  participantPositionsSelectOptions: getKnmErknmParticipantPositionsSelectOptions(state),
});

export const ErknmResultDecisionsItemContent = connect(mapStateToProps)(ErknmResultDecisionsItemContentWithoutStore);
